/**
 * 旅行回忆系统 - 旅行分享页面（公开，不需要权限）
 * 基于TravelDetail，但去掉编辑/删除功能
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { travelApi } from '../services/travelMemoriesApi';
import './TravelMemoriesTravelDetail.css';

const TravelMemoriesTravelShare = () => {
  const { travelId } = useParams();
  const [travel, setTravel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  useEffect(() => {
    if (travelId) {
      loadTravel();
    }
  }, [travelId]);

  const loadTravel = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 从URL参数获取token
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      
      if (!token) {
        setError('缺少分享token，请使用有效的分享链接');
        setLoading(false);
        return;
      }
      
      const response = await travelApi.getTravelByIdPublic(travelId, token);
      console.log('【TravelShare】API响应:', response);
      if (response && response.success) {
        setTravel(response.data);
      } else {
        setError(response?.message || '加载旅行详情失败');
      }
    } catch (error) {
      console.error('加载旅行详情失败:', error);
      setError(error.response?.data?.message || '加载失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 格式化日期（参考图：2025年2月2日）
  const formatDateLong = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    return `${y}年${m}月${d}日`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
    return `${month}月${day}日 ${weekdays[date.getDay()]}`;
  };

  // 地点展示（参考图：成都·都江堰、青城山）
  const locationLine =
    travel?.locations && travel.locations.length > 0
      ? travel.locations.length === 1
        ? travel.locations[0]
        : travel.locations[0] + '·' + travel.locations.slice(1).join('、')
      : '';

  // 计算旅行天数
  const calculateDays = () => {
    if (!travel?.startDate || !travel?.endDate) return 0;
    const start = new Date(travel.startDate);
    const end = new Date(travel.endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1;
  };

  if (loading) {
    return (
      <div className="tm-travel-detail-loading">
        <div className="tm-loading-spinner"></div>
        <div>加载中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tm-travel-detail-empty">
        <div>{error}</div>
      </div>
    );
  }

  if (!travel) {
    return (
      <div className="tm-travel-detail-empty">
        <div>旅行记录不存在</div>
      </div>
    );
  }

  const travelDays = calculateDays();

  // 按日期分组行程，并关联确幸语录
  const journeyByDate = {};
  if (travel.journey && travel.journey.length > 0) {
    travel.journey.forEach((item, index) => {
      const date = item.day || `Day ${index + 1}`;
      if (!journeyByDate[date]) {
        journeyByDate[date] = [];
      }
      journeyByDate[date].push(item);
    });
  }

  // 路线时间轴数据：优先 journey 按天，否则用 locations 作为节点
  const routeTimelineItems =
    travel.journey && travel.journey.length > 0
      ? Object.keys(journeyByDate)
          .sort()
          .map((date, i) => ({
            day: `D${i + 1}`,
            date,
            location: journeyByDate[date][0]?.location || '',
            activities: journeyByDate[date]
          }))
      : (travel.locations || []).map((loc, i) => ({
          day: `D${i + 1}`,
          date: '',
          location: loc,
          activities: [{ location: loc }]
        }));


  // Hero 背景：优先封面图，否则用第一张旅行照片
  const heroBackgroundUrl =
    travel.coverImage ||
    (travel.photos && travel.photos[0] && travel.photos[0].url) ||
    '';

  return (
    <div className="tm-travel-detail-new">
      {/* 1. Hero 大图：选一张照片做背景 + 白字标题与元信息 */}
      <section className="tm-detail-hero">
        <div
          className="tm-detail-hero-bg"
          style={{
            backgroundImage: heroBackgroundUrl
              ? `url('${heroBackgroundUrl.replace(/'/g, "%27")}')`
              : 'none',
            background: heroBackgroundUrl
              ? undefined
              : 'linear-gradient(135deg, #4a6572 0%, #2d3d47 100%)',
            backgroundSize: heroBackgroundUrl ? 'cover' : undefined,
            backgroundPosition: heroBackgroundUrl ? 'center' : undefined
          }}
        />
        <div className="tm-detail-hero-overlay" />
        <div className="tm-detail-hero-content">
          <div className="tm-detail-hero-info">
            <h1 className="tm-detail-hero-title">{travel.title || '未命名旅行'}</h1>
            {travel.subtitle && (
              <p className="tm-detail-hero-subtitle">{travel.subtitle}</p>
            )}
            <div className="tm-detail-hero-meta">
              <span className="tm-detail-meta-item">
                <span className="tm-detail-meta-icon">📅</span>
                {travel.startDate && formatDateLong(travel.startDate)} - {travel.endDate && formatDateLong(travel.endDate)}
              </span>
              {locationLine && (
                <span className="tm-detail-meta-item">
                  <span className="tm-detail-meta-icon">📍</span>
                  {locationLine}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 2. 关于这次旅行：浅色圆角卡片（参考图） */}
      {travel.description && (
        <section className="tm-detail-description-section">
          <div className="tm-detail-container">
            <div className="tm-detail-description-card">
              <p className="tm-detail-description-text">{travel.description}</p>
            </div>
          </div>
        </section>
      )}


      {/* 3. 旅行视频：标题+图标，视频位在圆角卡片内 */}
      {travel.videoUrl && (
        <section className="tm-detail-video-section">
          <div className="tm-detail-container">
            <h2 className="tm-detail-section-title tm-detail-section-title-with-icon">
              <span className="tm-detail-section-icon tm-detail-section-icon-video" />
              旅行视频
            </h2>
            <div className="tm-detail-video-card">
              <video
                className="tm-detail-video"
                src={travel.videoUrl}
                controls
                poster={travel.coverImage || undefined}
                playsInline
              />
            </div>
          </div>
        </section>
      )}

      {/* 4. 路线时间轴：展示路线信息 */}
      {routeTimelineItems.length > 0 && (
        <section className="tm-detail-route-section">
          <div className="tm-detail-container">
            <h2 className="tm-detail-section-title tm-detail-section-title-with-icon">
              <span className="tm-detail-section-icon tm-detail-section-icon-route" />
              旅行路线
            </h2>
            <div className="tm-detail-route-timeline">
              {routeTimelineItems.map((item, idx) => (
                <div key={idx} className="tm-detail-route-item">
                  <div className="tm-detail-route-day">{item.day}</div>
                  <div className="tm-detail-route-content">
                    <div className="tm-detail-route-location">{item.location}</div>
                    {item.date && (
                      <div className="tm-detail-route-date">{formatDate(item.date)}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 5. 旅行照片：网格布局 */}
      {travel.photos && travel.photos.length > 0 && (
        <section className="tm-detail-photos-section">
          <div className="tm-detail-container">
            <h2 className="tm-detail-section-title tm-detail-section-title-with-icon">
              <span className="tm-detail-section-icon tm-detail-section-icon-photo" />
              旅行照片 ({travel.photos.length}张)
            </h2>
            <div className="tm-detail-photos-masonry">
              {travel.photos.map((photo, idx) => (
                <div
                  key={photo.id || idx}
                  className="tm-detail-photo-card tm-detail-photo-card-clickable"
                  onClick={() => setLightboxPhoto(photo)}
                  onKeyDown={(e) => e.key === 'Enter' && setLightboxPhoto(photo)}
                  role="button"
                  tabIndex={0}
                  aria-label="查看大图"
                >
                  <img
                    src={photo.url}
                    alt={photo.title || `照片 ${idx + 1}`}
                    loading="lazy"
                  />
                  {(photo.title || photo.location) && (
                    <div className="tm-detail-photo-caption">
                      {photo.title && (
                        <div className="tm-detail-photo-caption-title">{photo.title}</div>
                      )}
                      {photo.location && (
                        <div className="tm-detail-photo-caption-location">{photo.location}</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 6. 确幸语录：卡片列表 */}
      {travel.moments && travel.moments.length > 0 && (
        <section className="tm-detail-moments-section">
          <div className="tm-detail-container">
            <h2 className="tm-detail-section-title tm-detail-section-title-with-icon">
              <span className="tm-detail-section-icon tm-detail-section-icon-moment" />
              确幸语录
            </h2>
            <div className="tm-detail-moments-grid">
              {travel.moments.map((moment, idx) => (
                <div
                  key={moment.id || idx}
                  className="tm-detail-moment-card"
                  style={{ borderLeftColor: moment.color || '#FFB6A3' }}
                >
                  <div className="tm-detail-moment-quote">{moment.quote}</div>
                  {moment.author && (
                    <div className="tm-detail-moment-author">— {moment.author}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 照片灯箱 */}
      {lightboxPhoto && (
        <div
          className="tm-detail-lightbox"
          onClick={() => setLightboxPhoto(null)}
        >
          <div className="tm-detail-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="tm-detail-lightbox-close"
              onClick={() => setLightboxPhoto(null)}
            >
              ×
            </button>
            <img src={lightboxPhoto.url} alt={lightboxPhoto.title || '照片'} />
            {lightboxPhoto.title && (
              <div className="tm-detail-lightbox-title">{lightboxPhoto.title}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelMemoriesTravelShare;
